'use client';

// @spec developer-tools-rum-metrics
import { datadogRum } from '@datadog/browser-rum';
import type { RumConfig } from './rum-config';

// Module-scope flag — survives StrictMode double-mount. The Datadog SDK
// itself ignores duplicate init() calls but logs a warning; guarding here
// keeps the dev console clean and matches the BugReportProvider pattern.
let initialized = false;

// Test-only override. When non-null, `isRumInitializedForEmit()` returns
// the override's `initialized` value instead of the real flag — letting
// tests gate emit paths (timings.ts, view-tags.ts) without invoking the
// real SDK init. Production code never sets this.
let testInitOverride: { initialized: boolean } | null = null;

/**
 * Initializes the Datadog RUM SDK with the provided config, skipping silently if already initialized or if config is null (RUM is disabled for this environment).
 */
export function initRum(config: RumConfig | null): void {
  if (config == null || initialized) {
    return;
  }
  // Set the initialized flag AFTER the SDK call lands so a thrown
  // init() doesn't leave the module stuck believing it succeeded —
  // every later initRum() call would otherwise short-circuit forever
  // for that tab. The pre-flight `if (initialized)` guard above still
  // protects against StrictMode double-mount because the second call
  // sees the flag set by the first synchronous return.
  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: config.env,
    version: config.version,
    sessionSampleRate: config.sessionSampleRate,
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    trackUserInteractions: config.trackUserInteractions,
    trackResources: config.trackResources,
    trackLongTasks: config.trackLongTasks,
    defaultPrivacyLevel: config.defaultPrivacyLevel,
    // FPO-900: RUM↔APM correlation. Pass-through to the SDK so fetch
    // calls landing on `/api/service/*` carry `traceparent` /
    // `x-datadog-trace-id` headers — server-side dd-trace continues
    // the trace and the FPO-806 dashboard surfaces a one-click jump
    // from a slow RUM session to its APM trace.
    allowedTracingUrls: config.allowedTracingUrls.map((entry) => ({
      match: entry.match,
      propagatorTypes: [...entry.propagatorTypes],
    })),
    traceSampleRate: config.traceSampleRate,
  });
  initialized = true;
}

/**
 * Returns the raw module-scope `initialized` flag. Used internally by the
 * `rum-actions.ts` test override to compose with its own gate. New emit
 * call sites should use `isRumInitializedForEmit()` below — it honors the
 * test override and is the canonical gate.
 */
export function isRumInitialized(): boolean {
  return initialized;
}

/**
 * Canonical gate for "should this RUM emission be allowed to land?".
 * Returns the test override when set, else the real `initialized` flag.
 *
 * Lives here (not in a separate module) so timings.ts, view-tags.ts, and
 * tests have one place to import from. AC8 of FPO-806 originally forbade
 * touching `init-rum.ts`; the review (issue #2) landed on "the canonical
 * module is the right home for the gate" — folding the override back in
 * eliminates the duplicate-pattern smell against `rum-actions.ts`'s own
 * `stateOverride`.
 */
export function isRumInitializedForEmit(): boolean {
  if (testInitOverride != null) {
    return testInitOverride.initialized;
  }
  return initialized;
}

/** Resets all module-scope initialization state so each test starts from a clean slate without bleed from prior suites. @visibleForTesting */
export function __resetRumInitStateForTests(): void {
  initialized = false;
  testInitOverride = null;
}

/** Injects a fake initialization state so tests can drive emit paths without invoking the real Datadog SDK; pass `null` to restore real-flag behaviour. @visibleForTesting */
export function __setRumInitStateOverrideForTests(state: { initialized: boolean } | null): void {
  testInitOverride = state;
}
